import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ReportViewModel } from 'types/report';
import { useNTUtilsContext } from './NTUtilsContext';

import { useNTScroll } from './NTScrollContext';
import { useNTContext } from './NTContext';
import { overflowShadowing, overflowShadowingDebounced } from './utils/sizingUtils';
import { useReportsStore } from 'hooks/useReportsStore';
import { useTableStore } from 'hooks/useTableStore';

export type cellStates = {
  hover: string | null;
  selected: string | null;
  selectedColumn: number | string | null;
  totalColumns: number;
};

const defaultReportViewModel: ReportViewModel = {
  liveReport: -1,
  totalColumn: false,
  difference: false,
  columns: [],
  locationId: null,
  locationName: null,
  timeFrame: 'Weekly',
  renderableColumns: 0,
  name: '',
  type: 'P&L Comparison',
  currency: 'USD',
  capabilities: {
    money: false,
    budget: false,
    percentage: false,
    transactions: false,
    charts: false,
    sparklines: 'none',
    smoothing: false,
    anomalies: false,
    totalRow: false,
    variance: false,
    synthetic: false,
    flaggedTransactions: false,
    liveReport: false,
  },
  utilityData: {
    totalColumn: {
      type: [{ type: 'Data', index: 0 }],
      data: undefined,
      originType: 'Data',
    },
    differenceColumn: { type: [{ type: 'Data', index: 0 }], data: undefined, originType: 'Data' },
  },
  periodTitles: null,
};

const NTSelectionContext = createContext<{
  transformedData: ReportViewModel;
  setReportViewModel: (transformedData: ReportViewModel | null) => void;
  cellStates: cellStates;
  setCellStates: (cellStates: cellStates) => void;
  isScrolling: boolean;
  setIsScrolling: React.Dispatch<React.SetStateAction<boolean>>;
  handleSelect: (newIndex: string, updateHistory: boolean) => void;
  handleColumnSelect: (columnId: number | string | null, centerColumn?: boolean) => void;
  updateSelectionHistory: (newItem: string) => void;
  selectRow: (scrollTo: boolean) => void;
  // New selectionHistory state and updater
  selectionHistory: string[];
  setSelectionHistory: React.Dispatch<React.SetStateAction<string[]>>;
  historyIndex: number;
  setHistoryIndex: React.Dispatch<React.SetStateAction<number>>;
}>({
  transformedData: defaultReportViewModel,
  setReportViewModel: () => {},
  cellStates: {
    hover: null,
    selected: null,
    selectedColumn: null,
    totalColumns: 0,
  },
  setCellStates: () => {},
  isScrolling: false,
  setIsScrolling: () => {},
  handleSelect: () => {},
  handleColumnSelect: () => {},
  updateSelectionHistory: () => {},
  selectRow: () => {},
  selectionHistory: [],
  setSelectionHistory: () => {},
  historyIndex: 0,
  setHistoryIndex: () => {},
});

export const useNTSelectionContext = () => useContext(NTSelectionContext);

export const NTSelectionProvider: React.FC<{
  children: React.ReactNode;
  initialSelection?: { rowID: string | null; columnID: string | null };
}> = ({ children, initialSelection }) => {
  const { actions, setActions, capabilities } = useNTUtilsContext();
  const { columns } = useNTContext();
  const { setScrollChange, setRefreshBrowsers, refreshBrowsers } = useNTScroll();
  const [transformedData, setReportViewModelState] = useState<ReportViewModel>(defaultReportViewModel);
  const [cellStates, setCellStatesState] = useState<cellStates>({
    hover: null,
    selected: initialSelection?.rowID ?? null,
    selectedColumn: initialSelection?.columnID ?? null,
    totalColumns: 0,
  });
  const setCellStates = useCallback((newCellStates: cellStates) => {
    setCellStatesState(newCellStates);
  }, []);
  const [isScrolling, setIsScrolling] = useState(false);
  const { aiReport, reportQueue } = useReportsStore(state => ({
    aiReport: state.aiReport,
    reportQueue: state.reportQueue,
  }));

  // New state variable for selection history
  const [selectionHistory, setSelectionHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Add new state for overflow
  const [hasOverflow, setHasOverflow] = useState(false);

  // Backend handles reverse via URL parameter - no state sync needed

  // Add overflow check when column count changes - use debounced version to prevent excessive DOM ops
  useEffect(() => {
    // Use debounced version to prevent expensive DOM operations during frequent updates
    // Only re-run when the number of columns actually changes, not on every render
    overflowShadowingDebounced(100);
  }, [transformedData.columns.length]);

  useEffect(() => {
    // Only respond to aiReport/reportQueue changes, not to detailsPanel changes from other sources
    // This prevents closing the panel when it's opened by cell clicks
    if (aiReport || reportQueue) {
      setActions(prevActions => ({ ...prevActions, detailsPanel: true }));
    }
    // Note: We don't auto-close the panel when aiReport/reportQueue becomes false
    // to allow manual panel control from cell clicks and other interactions
  }, [aiReport, reportQueue, setActions]);

  // Phase 3: Sync useTableStore selection with NTSelectionContext
  // This ensures RightPanel receives selection updates from NTCell
  const tableStoreSelectedCell = useTableStore(state => state.selectedCell);
  const tableStoreSelectedColumn = useTableStore(state => state.selectedColumn);

  useEffect(() => {
    // Sync selectedCell from store to context
    // Use setState callback to avoid stale closures
    setCellStatesState(prevStates => {
      if (tableStoreSelectedCell !== prevStates.selected) {
        return {
          ...prevStates,
          selected: tableStoreSelectedCell,
        };
      }
      return prevStates;
    });
  }, [tableStoreSelectedCell]);

  useEffect(() => {
    // Sync selectedColumn from store to context
    // Use setState callback to avoid stale closures
    setCellStatesState(prevStates => {
      if (tableStoreSelectedColumn !== prevStates.selectedColumn) {
        return {
          ...prevStates,
          selectedColumn: tableStoreSelectedColumn,
        };
      }
      return prevStates;
    });
  }, [tableStoreSelectedColumn]);

  // Add a helper function to update the selection history
  const updateSelectionHistory = (newItem: string) => {
    if (!newItem) return;
    setSelectionHistory(prevHistory => {
      // Remove the item if already in the history to avoid duplicates
      let newHistory = prevHistory;
      // Append the new item
      newHistory.push(newItem);
      // Enforce a maximum of 20 items by removing oldest items if necessary
      if (newHistory.length > 20) {
        newHistory = newHistory.slice(newHistory.length - 20);
      }
      return newHistory;
    });
    // Update the history index to the last item
    setHistoryIndex(selectionHistory.length - 1);
  };

  const scrollToSelectedRow = (newIndex: string) => {
    const tableYScroll = document.getElementById('table-y-scroll');
    const selectedRow = document.querySelector(`[data-row-id="${newIndex}"]`);
    if (tableYScroll && selectedRow) {
      const container = selectedRow.closest('.first-column') || selectedRow.parentElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const rowRect = selectedRow.getBoundingClientRect();
        const scrollOffset = rowRect.top - containerRect.top - tableYScroll.offsetHeight / 2;
        setScrollChange({ axis: 'y', amount: scrollOffset });
      }
    }
  };

  const scrollToSelectedColumn = (columnIndex: number | string | null) => {
    if (columnIndex === null || typeof columnIndex !== 'number') return;

    const tableMiddle = document.getElementById('table-middle');
    const headerMiddle = document.getElementById('header-middle');

    if (!tableMiddle || !headerMiddle) return;

    // Find a cell in the selected column (any row)
    const selectedCell = document.querySelector(`[data-column-id="${columnIndex}"]`);

    if (selectedCell) {
      // Get the column's position
      const cellRect = selectedCell.getBoundingClientRect();
      const tableRect = tableMiddle.getBoundingClientRect();

      // Calculate the scroll position to center the column
      // We need to account for the current scroll position
      const currentScrollLeft = tableMiddle.scrollLeft;
      const targetScrollLeft =
        currentScrollLeft + (cellRect.left - tableRect.left) - tableRect.width / 2 + cellRect.width / 2;

      // Update scroll position for both table and header
      tableMiddle.scrollLeft = targetScrollLeft;
      headerMiddle.scrollLeft = targetScrollLeft;

      // Notify the scroll system about the change
      setScrollChange({ axis: 'x', amount: targetScrollLeft });
    }
  };

  const selectRow = (scrollTo: boolean = false) => {
    const elementsSelected = document.querySelectorAll(`[data-row-id="${cellStates.selected}"]`);
    elementsSelected.forEach(el => el.classList.remove('selected'));
    if (cellStates.selected !== null) {
      const elementsNew = document.querySelectorAll(`[data-row-id="${cellStates.selected}"]`);
      elementsNew.forEach(el => el.classList.add('selected'));
      if (scrollTo) scrollToSelectedRow(cellStates.selected);
    }
  };

  const handleSelect = useCallback(
    (newIndex: string, updateHistory: boolean = true) => {
      setCellStatesState(prevStates => {
        if (prevStates.selected !== newIndex) {
          // Remove current selection style
          const elementsSelected = document.querySelectorAll(`[data-row-id="${prevStates.selected}"]`);
          elementsSelected.forEach(el => el.classList.remove('selected'));
          const elementsNew = document.querySelectorAll(`[data-row-id="${newIndex}"]`);
          elementsNew.forEach(el => el.classList.add('selected'));

          // Use the helper function to update the selection history
          if (updateHistory) updateSelectionHistory(newIndex);
          scrollToSelectedRow(newIndex);

          return {
            ...prevStates,
            selected: newIndex,
          };
        }
        return prevStates;
      });
    },
    [updateSelectionHistory]
  );

  // Map of keyboard shortcuts to module IDs
  // This needs to match the map in RightPanelContainer
  const shortcutMap: Record<string, string> = useMemo(
    () => ({
      g: 'chart',
      /*c: 'comments',
      k: 'ai-analysis',
      t: 'transactions', */
    }),
    []
  );

  // Handle column selection
  const handleColumnSelect = useCallback(
    (columnIndex: number | string | null, centerColumn: boolean = false) => {
      setCellStatesState(prevStates => {
        if (columnIndex === null) {
          // If no column is selected, just clear the selection
          return {
            ...prevStates,
            selectedColumn: null,
          };
        }

        // Get total columns (excluding the first column which is for labels/accounts)
        const totalDataColumns = transformedData.columns.length - 1;

        // Only scroll to center the column if explicitly requested
        if (centerColumn) {
          // Pass the adjusted column index to the scroll function
          scrollToSelectedColumn(columnIndex);
        }

        // Backend handles column reversal - data already in correct order
        // We store the real column index in the cell state
        return {
          ...prevStates,
          selectedColumn: columnIndex,
          totalColumns: totalDataColumns,
        };
      });
    },
    [transformedData.columns.length]
  );

  // We'll handle shortcuts directly in the keyboard event handler
  // This avoids circular dependencies between contexts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't process shortcuts if a modal is open or during text input
      if (actions.trxModal || e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Check for module shortcuts
      // We'll handle these directly here to avoid circular dependencies
      // This matches the shortcuts defined in module configs
      // TODO: Refactor this to use module configs dynamically and check if modules are enabled
      const moduleId = shortcutMap[e.key];
      if (moduleId) {
        e.preventDefault();

        // Store the last pressed shortcut key in a custom event
        // RightPanelContainer will listen for this event and set the active module
        const shortcutEvent = new CustomEvent('module-shortcut', {
          detail: { key: e.key, moduleId },
        });
        document.dispatchEvent(shortcutEvent);

        // We only handle opening the panel here
        // RightPanelContainer will handle panel closing
        if (!actions.detailsPanel) {
          setActions({ ...actions, detailsPanel: true });
        }

        return;
      }

      // If no data or no selection, don't process arrow keys
      if (!transformedData.columns[0]?.data || !cellStates.selected) {
        return;
      }

      // Handle arrow keys for navigation
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
      }

      switch (e.key) {
        case 'ArrowUp':
          if (cellStates.selected) {
            const currentRow = document.querySelector(`[data-row-id="${cellStates.selected}"]`);
            if (currentRow) {
              const prevRow = currentRow.previousElementSibling;
              if (prevRow && prevRow.getAttribute('data-row-id')) {
                handleSelect(prevRow.getAttribute('data-row-id') as string);
              }
            }
          }
          break;
        case 'ArrowDown':
          if (cellStates.selected) {
            const currentRow = document.querySelector(`[data-row-id="${cellStates.selected}"]`);
            if (currentRow) {
              const nextRow = currentRow.nextElementSibling;
              if (nextRow && nextRow.getAttribute('data-row-id')) {
                handleSelect(nextRow.getAttribute('data-row-id') as string);
              }
            }
          }
          break;
        case 'ArrowLeft':
          // Handle left arrow key for column navigation
          if (typeof cellStates.selectedColumn === 'number') {
            if (cellStates.selectedColumn > 1) {
              // Pass true to center the column when using keyboard navigation
              handleColumnSelect(cellStates.selectedColumn - 1, true);
            }
          }
          break;
        case 'ArrowRight':
          // Handle right arrow key for column navigation
          if (typeof cellStates.selectedColumn === 'number') {
            // Get the maximum column index (columns.length - 1)
            const maxColumnIndex = transformedData.columns.length - 1;
            // Ensure we don't go beyond the maximum column index
            const nextColumnIndex = Math.min(cellStates.selectedColumn + 1, maxColumnIndex);
            // Pass true to center the column when using keyboard navigation
            //handleColumnSelect(nextColumnIndex, true);

            if (cellStates.selectedColumn < maxColumnIndex) {
              // Pass true to center the column when using keyboard navigation
              handleColumnSelect(cellStates.selectedColumn + 1, true);
            }
          } else if (cellStates.selectedColumn === null) {
            // If no column is selected, select the first one
            handleColumnSelect(1, true);
          }
          break;

        case 'Enter':
          if (capabilities.charts && columns > 2) {
            e.preventDefault();
            setActions({ ...actions, detailsPanel: !actions.detailsPanel });
            setTimeout(() => {
              setRefreshBrowsers(r => !r);
            }, 12);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    transformedData,
    cellStates,
    actions,
    setActions,
    capabilities.charts,
    columns,
    handleSelect,
    handleColumnSelect,
    shortcutMap,
    isScrolling,
    setRefreshBrowsers,
  ]);

  const setReportViewModel = useCallback((newData: ReportViewModel | null) => {
    if (newData !== null) {
      setReportViewModelState(newData);
    }
  }, []);

  const value = useMemo(
    () => ({
      transformedData,
      setReportViewModel,
      cellStates,
      setCellStates,
      isScrolling,
      setIsScrolling,
      handleSelect,
      handleColumnSelect,
      updateSelectionHistory,
      selectRow,
      // Expose selectionHistory and its updater
      selectionHistory,
      setSelectionHistory,
      historyIndex,
      setHistoryIndex,
    }),
    [
      transformedData,
      setReportViewModel,
      cellStates,
      setCellStates,
      isScrolling,
      setIsScrolling,
      handleSelect,
      handleColumnSelect,
      updateSelectionHistory,
      selectRow,
      selectionHistory,
      setSelectionHistory,
      historyIndex,
      setHistoryIndex,
    ]
  );

  return <NTSelectionContext.Provider value={value}>{children}</NTSelectionContext.Provider>;
};
